import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Image } from '@/atoms/Image'
import { AsH2, AsH3, AsH4, CaptionLG, HeadingLG, ParagraphMD, TitleLG, TitleMD } from '@/atoms/Text'
import { options } from '@/services/ContentfulService'
import { GuildFeaturedSection as GuildFeaturedSectionData } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isDefined } from '@/utils/primitives/generics'

interface GuildFeaturedSectionProps {
  guildFeaturedSection?: GuildFeaturedSectionData | null
}

export const GuildFeaturedSection: React.FC<GuildFeaturedSectionProps> = ({ guildFeaturedSection }) => {
  if (!guildFeaturedSection) return null

  const featuredItems = guildFeaturedSection?.featuredItemsCollection?.items.filter(isDefined) ?? []

  return (
    <section id="cast" className="px-6 py-[104px] lg:px-10">
      {guildFeaturedSection?.heading && (
        <HeadingLG as={AsH2} weight="bold" color="white" className="mb-6 text-center">
          {guildFeaturedSection.heading}
        </HeadingLG>
      )}

      {guildFeaturedSection?.subHeading && (
        <div className="mb-6 py-3">
          <CaptionLG weight="semibold" color="white" className="text-center">
            {guildFeaturedSection.subHeading}
          </CaptionLG>
        </div>
      )}
      {featuredItems.map((item, index) => {
        const backgroundImageUrl = item.backgroundImageUrl ?? ''
        const featuredImageUrl = item.featuredImageUrl ?? ''
        const heading = item.heading ?? ''
        const description = item.description ?? ''
        const avatarImageUrl = item.avatarImageUrl ?? ''
        const avatarTitle = item.avatarTitle ?? ''
        const avatarDescription = item.avatarDescription ?? null

        return (
          <div
            key={item.sys.id}
            className={`flex flex-col bg-cover md:flex-row ${
              index % 2 === 1 ? 'md:flex-row-reverse' : ''
            } mb-8 items-center gap-8 rounded-2xl p-8`}
            style={{
              backgroundImage: backgroundImageUrl
                ? `url(${getCloudinaryImageUrl({ path: backgroundImageUrl })})`
                : undefined,
            }}
          >
            <div className="flex-1 space-y-6">
              <div>
                {heading && (
                  <TitleLG
                    as={AsH3}
                    weight="bold"
                    color="white"
                    className="mb-2 flex items-center justify-center gap-2 md:justify-start"
                  >
                    {heading}
                  </TitleLG>
                )}
                {featuredImageUrl && (
                  <div className="flex-1 lg:hidden">
                    <Image
                      src={getCloudinaryImageUrl({
                        path: featuredImageUrl,
                        height: 400,
                        width: 400,
                      })}
                      alt={heading}
                      className="mx-auto max-h-[400px] w-full max-w-fit"
                      height={400}
                      width={400}
                      loading="lazy"
                      sizes="(max-width: 1023px) min(400px, 90vw), 0px"
                    />
                  </div>
                )}
                {description && (
                  <ParagraphMD weight="medium" color="core-gray-200" className="text-center lg:text-left">
                    {description}
                  </ParagraphMD>
                )}
                {(avatarImageUrl || avatarTitle) && (
                  <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row">
                    {avatarImageUrl && (
                      <Image
                        src={getCloudinaryImageUrl({
                          path: avatarImageUrl,
                          height: 128,
                        })}
                        alt={avatarTitle}
                        className="h-32 w-32 rounded-full border-4 border-black object-cover"
                        height={128}
                        width={128}
                        loading="lazy"
                        sizes="128px"
                      />
                    )}
                    {avatarTitle && (
                      <div>
                        <TitleMD
                          as={AsH4}
                          weight="semibold"
                          color="white"
                          className="flex items-center gap-2 font-semibold"
                        >
                          {avatarTitle}
                        </TitleMD>
                        {avatarDescription?.json && (
                          <div className="mt-4 text-white">
                            {documentToReactComponents(avatarDescription?.json, options)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {featuredImageUrl && (
              <div className="hidden flex-1 lg:block">
                <Image
                  src={getCloudinaryImageUrl({
                    path: featuredImageUrl,
                    height: 400,
                  })}
                  alt={heading}
                  className="mx-auto max-h-[400px] w-full max-w-fit"
                  height={400}
                  width={400}
                  loading="lazy"
                  sizes="(min-width: 1024px) min(400px, 33vw), 0px"
                />
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
